import {
  caseCategoryEnum,
  caseClassificationEnum,
  casePartyRoleEnum,
  caseTypeEnum,
  governmentalOrganizations,
  partyTypeEnum,
  socialIdentityTypeEnum,
  sharedAssetTypeEnum,
} from '@/helpers/enum';
import { deepClone, uuidGenerator, isValidMiladyDate } from '@/helpers/utils';
import ar from '@/local/ar.js';

export const oneOrMoreExists = length => length > 0;
export const isAtLeastOneExists = length => length <= 1;
export const isNoOneExists = length => length <= 0;

export const requiredDefendantInCaseCategory = (caseTypes, caseTypeId) => {
  return !(caseTypes || []).find(caseType => caseType.id === caseTypeId).allowNoDefendants;
};
export const calculateAge = (birthday) => {
  if (!isValidMiladyDate(birthday)) return 0;
  const diff = (new Date() - new Date(birthday)) / (1000 * 60 * 60 * 24 * 354.367056);
  return diff < 0 ? 0 : diff;
};
export const isRole = (casePartyRole, role) =>
  Number(casePartyRole) === Number(casePartyRoleEnum[role]);
export const isInRole = (caseParty, role) => caseParty && isRole(caseParty.casePartyRole, role);
export const isRelativeInRole = (caseParty, role) => ((caseParty || {}).relatedPartiesInfo || []).some(rpi => isInRole(rpi, role));
export const isProsecutor = caseParty => isInRole(caseParty, 'prosecutor');
export const isProsecutorRole = casePartyRole => isRole(casePartyRole, 'prosecutor');
export const isDefendant = caseParty => isInRole(caseParty, 'defendant');
export const isDefendantRole = casePartyRole => isRole(casePartyRole, 'defendant');
export const isProsecutorOrDefendant = caseParty =>
  caseParty && (isProsecutor(caseParty) || isDefendant(caseParty));
export const isRepresentative = caseParty =>
  Boolean(caseParty) &&
  Boolean(caseParty.relatedPartiesInfo) &&
  Boolean(caseParty.relatedPartiesInfo.length);
export const isAllowedAsRepresentativeForOrganizationOwner = caseParty =>
  isRepresentative(caseParty) && isCessationObserver(caseParty.relatedPartiesInfo.find(rpi => rpi.casePartyObjectKey === caseParty.casePartyObjectKey));
export const isAgent = caseParty => isInRole(caseParty, 'agent');
export const isLawyer = caseParty => (isInRole(caseParty, 'lawyer') || isRelativeInRole(caseParty, 'lawyer'));
export const isRepresentativeRole = caseParty => isInRole(caseParty, 'representative');
export const isRegent = caseParty => isInRole(caseParty, 'regent');
export const isGuardian = caseParty => isInRole(caseParty, 'guardian');
export const isLiquidator = caseParty => isInRole(caseParty, 'liquidator');
export const isLiquidatorOrJudicialGuardRole = caseParty => isInRole(caseParty, 'liquidator') || isInRole(caseParty, 'judicialGuard');
export const isInheritor = caseParty => isOfType(caseParty, 'person') && caseParty.isInheritor;
export const isCessationObserver = caseParty => isInRole(caseParty, 'cessationObserver');
export const isPersonNotInheritor = caseParty => isPerson(caseParty) && !isInheritor(caseParty);

export const filterCasePartiesByRole = (caseParties, role) => caseParties.filter(caseParty => isInRole(caseParty, role));

export const isProsecutorRepresentative = caseParty =>
  isRepresentative(caseParty) &&
  isProsecutorRole(caseParty.relatedPartiesInfo[0].relatedCasePartyRole);
export const isProsecutorRepresentativeRepresentative = caseParty =>
  isRepresentative(caseParty) &&
  isProsecutorRole(caseParty.relatedPartiesInfo[0].relatedOfRelatedCasePartyRole);
export const isProsecutorOrProsecutorRepresentative = caseParty =>
  isProsecutor(caseParty) || isProsecutorRepresentative(caseParty) || isProsecutorRepresentativeRepresentative(caseParty);
export const isDefendantRepresentative = caseParty =>
  isRepresentative(caseParty) &&
  isDefendantRole(caseParty.relatedPartiesInfo[0].relatedCasePartyRole);
export const isDefendantRepresentativeRepresentative = caseParty =>
  isRepresentative(caseParty) &&
  isDefendantRole(caseParty.relatedPartiesInfo[0].relatedOfRelatedCasePartyRole);
export const isDefendantOrDefendantRepresentative = caseParty =>
  isDefendant(caseParty) || isDefendantRepresentative(caseParty) || isDefendantRepresentativeRepresentative(caseParty);
export const isNotPersonRepresentative = (caseParty) => {
  const relatedCaseParty = getRelatedCasePartyByRelatedObjectKey(
    caseParty,
    caseParty.casePartyObjectKey
  );
  return (
    isRepresentative(caseParty) &&
    relatedCaseParty &&
    isNotType(relatedCaseParty.relatedPartyType, 'person')
  );
};

export const isType = (casePartyType, type) => casePartyType === partyTypeEnum[type];
export const isNotType = (casePartyType, type) => casePartyType !== partyTypeEnum[type];
export const isOfType = (caseParty, partyType) =>
  caseParty && isType(caseParty.partyType, partyType);
export const isPerson = caseParty => isOfType(caseParty, 'person') && !isOrganizationOwner(caseParty);
export const isFirm = caseParty => isOfType(caseParty, 'firm');
export const isOrganization = caseParty => isOfType(caseParty, 'organization') || isOrganizationOwner(caseParty);
export const isOrganizationOwner = caseParty => isOfType(caseParty, 'person') && caseParty.isOrganizationOwner;
export const isPersonOrOrganizationOwner = caseParty => isPerson(caseParty) || isOrganizationOwner(caseParty);
export const isGovernment = caseParty => isOfType(caseParty, 'government');
export const isEmbassy = caseParty => isOfType(caseParty, 'embassy');
export const isAssociation = caseParty => isOfType(caseParty, 'association');
export const isInstitutionWithoutCommercialRegisterNo = caseParty =>
  isOfType(caseParty, 'institutionWithoutCommercialRegisterNo');
export const isHumanSociety = caseParty => isOfType(caseParty, 'humanSociety');
export const isForeignCompany = caseParty => isOfType(caseParty, 'foreignCompany');
export const isCessation = caseParty => isOfType(caseParty, 'cessation');
export const isLiquidatorType = caseParty => isOfType(caseParty, 'liquidator');
export const isJudicialGuardType = caseParty => isOfType(caseParty, 'judicialGuard');
export const isLiquidatorOrJudicialGuard = caseParty => isLiquidatorType(caseParty) || isJudicialGuardType(caseParty);

export const isSaudiType = socialIdType =>
  socialIdType === socialIdentityTypeEnum.nationalId ||
  socialIdType === socialIdentityTypeEnum.nationalIdBooklet;

export const isSaudi = (caseParty, propertyName) => isSaudiType(caseParty[propertyName || 'socialIdType']);

export const isResident = (caseParty) => {
  const residenceIds = [socialIdentityTypeEnum.regularResidency];
  return isSaudiType(caseParty.socialIdType) || residenceIds.indexOf(caseParty.socialIdType) !== -1;
};

export const isDead = caseParty => caseParty.lifeStatusId === 1;

export const isCriminalLawsuit = caseData =>
  Boolean(caseData) &&
  [
    caseCategoryEnum.criminalCases,
    caseCategoryEnum.criminalCases2,
    caseCategoryEnum.cooperativeCriminal,
    caseCategoryEnum.individualCriminal,
    caseCategoryEnum.retributionAndNonSequentialLimitsIndividual,
    caseCategoryEnum.senderPunishmentIndividual,
    caseCategoryEnum.organizedPunishmentIndividual,
    caseCategoryEnum.juvenile,
    caseCategoryEnum.retributionAndLimitsOfDamageCooperative,
    caseCategoryEnum.generalPunishmentCooperative,
    caseCategoryEnum.organizedPunishmentCooperative,
    caseCategoryEnum.financialAndAdministrativeCorruption,
  ].indexOf(caseData.caseCategoryId) !== -1
  || caseData.caseClassificationIdNew === caseClassificationEnum.criminal;

export const isLaborLawsuit = caseData =>
  Boolean(caseData) && (caseData.caseCategoryId === caseCategoryEnum.workers
    || caseData.caseClassificationIdNew === caseClassificationEnum.labor);
export const isAppealLawsuit = caseData =>
  Boolean(caseData) && (caseData.activateCourtOfAppeal === true);
export const isPersonalLawsuit = caseData =>
  Boolean(caseData) && (caseData.caseCategoryId === caseCategoryEnum.personalStatusEnding
    || caseData.caseClassificationIdNew === caseClassificationEnum.family);
export const isInspectionLawsuit = (caseData) => {
  return (
    Boolean(caseData) && (caseData.caseTypeId === caseTypeEnum.laborInspectionToProveTheCondition
      || caseData.caseTypeId === caseTypeEnum.generalInspectionToProveTheCondition
      || caseData.caseTypeId === caseTypeEnum.criminalInspectionToProveTheCondition));
};
export const isGeneralLawsuit = caseData =>
  Boolean(caseData) && (caseData.caseCategoryId === caseCategoryEnum.personalStatusEnding
    || caseData.caseClassificationIdNew === caseClassificationEnum.general);
export const isCommercialLawsuit = caseData =>
  Boolean(caseData) && (caseData.caseCategoryId === caseCategoryEnum.commercial
    || caseData.caseClassificationIdNew === caseClassificationEnum.commercial);
export const isExecutionLawsuit = caseData =>
  Boolean(caseData) && caseData.caseClassificationIdNew === caseClassificationEnum.execution;
export const isLaborOrCriminalLawsuit = caseData =>
  isLaborLawsuit(caseData) || isCriminalLawsuit(caseData);

export const isTerminationsLawsuit = caseData => Boolean(caseData) && caseData.caseTypeId === caseTypeEnum.guardianshipOverAmentallyMinor;

export const isTrafficLawsuit = (caseData) => {
  return (
    Boolean(caseData) &&
    caseData.caseCategoryId === caseCategoryEnum.trafficCases &&
    (caseData.caseTypeId === caseTypeEnum.traffic ||
      caseData.caseTypeId === caseTypeEnum.deathDueToAccident)
  );
};
export const isTrafficAccidentLocation = (caseData) => {
  return (
    Boolean(caseData) &&
    caseData.caseCategoryId === caseCategoryEnum.trafficCases &&
    (caseData.caseTypeId === caseTypeEnum.traffic ||
      caseData.caseTypeId === caseTypeEnum.proveTrafficAccidentErrorRate ||
      caseData.caseTypeId === caseTypeEnum.deathDueToAccident)
    || caseData.caseTypeId === caseTypeEnum.privateRightBasedOnAccident
    || caseData.caseTypeId === caseTypeEnum.publicRightBasedOnAccident
  );
};

export const isCasePartyNotNeedRepresentative = caseParty =>
  [
    governmentalOrganizations.publicProsecution['id'],
    governmentalOrganizations.NationalAntiCorruptionCommission['id'],
    governmentalOrganizations.NationalAntiCorruptionCommissionOld['id'],
  ].includes((caseParty || {}).organizationId);

export const isCustodyLawsuit = caseData =>
  Boolean(caseData) && caseData.caseTypeId === caseTypeEnum.custody;

export const isGeneralRight = caseData => !!caseData.isGeneralRight;

export const isCasePartyExists = (casePartyId, casePartyIds) => {
  return casePartyIds.some(cpId => cpId.uniqueId === casePartyId.uniqueId);
};

export const isSameCaseParties = (casePartyId1, casePartyId2) =>
  casePartyId1.uniqueId === casePartyId2.uniqueId;

export const isCasePartyExistsExceptSelected = (casePartyId, casePartyIds) =>
  casePartyIds.some(
    cpId => cpId.uniqueId === casePartyId.uniqueId && cpId.objectKey !== casePartyId.objectKey
  );

export const arabNationalities = [
  {
    id: 1,
    name: 'السعودية',
  },
  {
    id: 2,
    name: 'البحرين',
  },
  {
    id: 3,
    name: 'قطر',
  },
  {
    id: 4,
    name: 'عمان',
  },
  {
    id: 5,
    name: 'الامارات',
  },
  {
    id: 6,
    name: 'الكويت',
  },
  {
    id: 7,
    name: 'العراق',
  },
  {
    id: 8,
    name: 'اليمن',
  },
  {
    id: 10,
    name: 'فلسطين',
  },
  {
    id: 11,
    name: 'سوريا',
  },
  {
    id: 12,
    name: 'لبنان',
  },
  {
    id: 13,
    name: 'مصر',
  },
  {
    id: 14,
    name: 'السودان',
  },
  {
    id: 16,
    name: 'الصومال',
  },
  {
    id: 17,
    name: 'جيبوتي',
  },
  {
    id: 18,
    name: 'موريتانيا',
  },
  {
    id: 19,
    name: 'تونس',
  },
  {
    id: 20,
    name: 'الجزائر',
  },
  {
    id: 21,
    name: 'المغرب',
  },
  {
    id: 101,
    name: 'جزر القمر',
  },
  {
    id: 204,
    name: 'الاردن',
  },
  {
    id: 9,
    name: 'الاردن',
  },
  {
    id: 22,
    name: 'ليبيا',
  },
  {
    id: 453,
    name: 'جمهورية جنوب السودان',
  },
];
export const isArabNationality = (nationalityId) => {
  return arabNationalities.find(arabNationality => arabNationality.id === nationalityId);
};

export const getCasePartyIdAndName = caseParty => ({
  id: getCasePartyId(caseParty),
  name: getCasePartyName(caseParty),
});

export const getCasePartyId = (casePartyObj) => {
  if (!casePartyObj) return {};
  const caseParty = deepClone(casePartyObj);
  const returnId = {
    id: null,
    type: null,
    uniqueId: null,
    objectKey: caseParty.objectKey,
    // tODO: remove casePartyRole property from here
    casePartyRole: caseParty.casePartyRole,
  };

  if (isRepresentative(caseParty) && !isProsecutorOrDefendant(caseParty)) {
    returnId.objectKey = caseParty.relatedPartiesInfo[0].objectKey;
  }

  if (caseParty.isOrganizationOwner) caseParty.partyType = partyTypeEnum.organization;

  switch (caseParty.partyType) {
    case partyTypeEnum.firm:
    case partyTypeEnum.organization:
    case partyTypeEnum.foreignCompany:
      returnId.id = caseParty.unifiedNumber || caseParty.commercialRegistrationNumber;
      returnId.type = caseParty.unifiedNumber ? socialIdentityTypeEnum.unifiedNumber : socialIdentityTypeEnum.commercialRegistrationNumber;
      returnId.uniqueId = caseParty.unifiedNumber || caseParty.commercialRegistrationNumber;
      break;
    case partyTypeEnum.institutionWithoutCommercialRegisterNo:
      returnId.id = caseParty.unifiedNumber || caseParty.commercialRegistrationNumber;
      returnId.type = caseParty.unifiedNumber ? socialIdentityTypeEnum.unifiedNumber : socialIdentityTypeEnum.organizationId;
      returnId.uniqueId = caseParty.unifiedNumber || caseParty.commercialRegistrationNumber;
      break;
    case partyTypeEnum.association:
    case partyTypeEnum.government:
    case partyTypeEnum.embassy:
      returnId.id = caseParty.unifiedNumber || caseParty.organizationId;
      returnId.type = caseParty.unifiedNumber ? socialIdentityTypeEnum.unifiedNumber : undefined;
      returnId.uniqueId = caseParty.unifiedNumber || caseParty.organizationId;
      break;
    case partyTypeEnum.humanSociety:
      returnId.id = caseParty.name;
      returnId.uniqueId = caseParty.name;
      break;
    case partyTypeEnum.cessation:
      returnId.id = caseParty.cessationName;
      returnId.type = partyTypeEnum.cessation;
      returnId.uniqueId = caseParty.cessationName;
      break;
    case partyTypeEnum.liquidator:
    case partyTypeEnum.judicialGuard:
      returnId.id = caseParty.sharedAssetType === sharedAssetTypeEnum.commonAsset ? caseParty.name : caseParty.socialId;
      returnId.type = caseParty.partyType;
      returnId.uniqueId = caseParty.sharedAssetType === sharedAssetTypeEnum.commonAsset
        ? `${caseParty.name}-${caseParty.partyType}`
        : `${caseParty.socialIdType}${caseParty.socialId}-${caseParty.partyType}`;

      break;
    default:
      returnId.id = caseParty.socialId;
      returnId.type = caseParty.socialIdType;
      returnId.uniqueId = `${caseParty.socialIdType}${caseParty.socialId}`;
      break;
  }

  return returnId;
};

export const getCasePartyName = (caseParty, lookup = []) => {
  if (!caseParty) return '';
  let name;

  switch (caseParty.partyType) {
    case partyTypeEnum.person:
      name = `${caseParty.firstName ? caseParty.firstName + ' ' : ''}${caseParty.fatherName ? caseParty.fatherName + ' ' : ''}${caseParty.grandFatherName ? caseParty.grandFatherName + ' ' : ''}${caseParty.familyName ? caseParty.familyName : ''}`;
      break;
    case partyTypeEnum.government: {
      const agency = lookup.find(g => g.id === caseParty.organizationId);
      name = agency ? agency.organizationName : ar.governmentAttorney.governmentAttorneyName;
      break;
    }
    case partyTypeEnum.embassy: {
      name = caseParty.name;
      if (!name) {
        const embassy = lookup.find(g => g.id === caseParty.organizationId);
        name = embassy ? embassy.organizationName : ar.embassy.name;
      }
      break;
    }
    case partyTypeEnum.association: {
      const association = lookup.find(g => g.id === caseParty.organizationId);
      name = association ? association.organizationName : ar.association.associationName;
      break;
    }
    case partyTypeEnum.cessation: {
      name = caseParty.cessationName;
      break;
    }
    case partyTypeEnum.organization: {
      const managerName = `${caseParty.firstName ? caseParty.firstName + ' ' : ''}${caseParty.fatherName ? caseParty.fatherName + ' ' : ''}${caseParty.grandFatherName ? caseParty.grandFatherName + ' ' : ''}${caseParty.familyName ? caseParty.familyName : ''}`;
      if (!managerName)
        name = caseParty.name;
      else
        name = managerName;
      break;

    }
    case partyTypeEnum.liquidator: {
      if (caseParty.sharedAssetType === sharedAssetTypeEnum.commonAsset)
        name = `مصفي ${caseParty.name}`;
      else if (caseParty.sharedAssetType === sharedAssetTypeEnum.inheritance)
        name = `مصفي تركة ${caseParty.firstName ? caseParty.firstName + ' ' : ''}${caseParty.fatherName ? caseParty.fatherName + ' ' : ''}${caseParty.grandFatherName ? caseParty.grandFatherName + ' ' : ''}${caseParty.familyName ? caseParty.familyName : ''}`;
      break;
    }
    case partyTypeEnum.judicialGuard: {
      if (caseParty.sharedAssetType === sharedAssetTypeEnum.commonAsset)
        name = `حارس قضائي ${caseParty.name}`;
      else if (caseParty.sharedAssetType === sharedAssetTypeEnum.inheritance)
        name = `حارس قضائي تركة ${caseParty.firstName ? caseParty.firstName + ' ' : ''}${caseParty.fatherName ? caseParty.fatherName + ' ' : ''}${caseParty.grandFatherName ? caseParty.grandFatherName + ' ' : ''}${caseParty.familyName ? caseParty.familyName : ''}`;
      break;
    }
    case partyTypeEnum.firm:
    case partyTypeEnum.humanSociety:
    case partyTypeEnum.institutionWithoutCommercialRegisterNo:
    case partyTypeEnum.foreignCompany:
      name = caseParty.name;
      break;
  }

  return name || '';
};

export const getFirstAndLastName = (caseParty) => {
  if (!caseParty) return '';
  return `${caseParty.firstName || ''} ${caseParty.familyName || ''}`;
};

export const convertCasePartyToCasePartyRepresentative = (
  casePartyRepresentativeRole,
  relatedCaseParty
) => {
  let casePartyRepresentative = {
    objectKey: uuidGenerator(),
    casePartyRole: casePartyRepresentativeRole,
    casePartyObjectKey: relatedCaseParty.objectKey,
    socialId: relatedCaseParty.socialId,
    dateOfBirth: relatedCaseParty.dateOfBirth,
    socialIdType: relatedCaseParty.socialIdType,
    organizationId: relatedCaseParty.organizationId,
    commercialRegistrationNumber: relatedCaseParty.commercialRegistrationNumber,
    companyName: relatedCaseParty.name || relatedCaseParty.cessationName || (relatedCaseParty.sharedAssetType === sharedAssetTypeEnum.inheritance ? [
      relatedCaseParty.firstName || '',
      relatedCaseParty.fatherName || '',
      relatedCaseParty.grandFatherName || '',
      relatedCaseParty.familyName || '',
    ].filter(part => part)
      .join(' ') : ''),
    relatedCasePartyRole: relatedCaseParty.casePartyRole,
    relatedPartyType: relatedCaseParty.partyType,
    unifiedNumber: relatedCaseParty.unifiedNumber,
    isOrganizationOwner: relatedCaseParty.isOrganizationOwner,
    sharedAssetType: relatedCaseParty.sharedAssetType,
  };

  const relatedOfRelatedCaseParty = relatedCaseParty.relatedPartiesInfo.find(
    rp => rp.casePartyObjectKey === relatedCaseParty.casePartyObjectKey
  );
  if (relatedOfRelatedCaseParty) {
    casePartyRepresentative = {
      ...casePartyRepresentative,
      relatedOfRelatedCasePartyObjectKey: relatedOfRelatedCaseParty.casePartyObjectKey,
      relatedOfRelatedCasePartyRole: relatedOfRelatedCaseParty.relatedCasePartyRole,
      relatedOfRelatedPartyType: relatedOfRelatedCaseParty.relatedPartyType,
    };
  }

  return casePartyRepresentative;
};

export const getCasePartyByObjectKey = (caseParties, objectKey) => {
  const caseParty = caseParties.find(cp => cp.objectKey === objectKey);
  return caseParty ? deepClone(caseParty) : null;
};

export const getRepresentativeByObjectKey = (caseParties, objectKey) => {
  const casePartyRepresentative = caseParties.find(cp =>
    cp.relatedPartiesInfo.some(rp => rp.casePartyObjectKey === objectKey)
  );
  return casePartyRepresentative ? deepClone(casePartyRepresentative) : null;
};

export const getRepresentativeByRelatedObjectKey = (caseParties, objectKey) => {
  const tempCasePartyRepresentative = caseParties.find(cp =>
    cp.relatedPartiesInfo.some(rp => rp.objectKey === objectKey)
  );

  if (tempCasePartyRepresentative) {
    const casePartyRepresentative = deepClone(tempCasePartyRepresentative);
    const relatedCaseParty = getRelatedCasePartyByObjectKey(casePartyRepresentative, objectKey);
    casePartyRepresentative.casePartyObjectKey = relatedCaseParty.casePartyObjectKey;
    return casePartyRepresentative;
  }
  return null;
};

export const getCasePartyOrRepresentativeByObjectKey = (caseParties, objectKey) =>
  getCasePartyByObjectKey(caseParties, objectKey) ||
  getRepresentativeByRelatedObjectKey(caseParties, objectKey);

export const getRelatedCasePartyByObjectKey = (caseParty, objectKey) => {
  const relatedCaseParty = caseParty.relatedPartiesInfo.find(rp => rp.objectKey === objectKey);
  return relatedCaseParty ? deepClone(relatedCaseParty) : null;
};

export const getRelatedCasePartyByRelatedObjectKey = (caseParty, casePartyObjectKey) => {
  const relatedCaseParty = (caseParty.relatedPartiesInfo || []).find(
    rp => rp.casePartyObjectKey === casePartyObjectKey
  );
  return relatedCaseParty || null;
};

export const getRepresentativeObjectKeyByRelatedObjectKey = (caseParty, casePartyObjectKey) => {
  const relatedCaseParty = getRelatedCasePartyByRelatedObjectKey(caseParty, casePartyObjectKey);
  return relatedCaseParty ? relatedCaseParty.objectKey : null;
};

export const refineCasePartiesAfterAPI = caseParties =>
  caseParties.map((cp) => {
    if (!cp.workAddress) cp.workAddress = {};
    if (!cp.residenceAddress) cp.residenceAddress = {};
    if (!cp.placeAddress) cp.placeAddress = {};
    if (!cp.nationalAddress) cp.nationalAddress = {};
    if (!cp.contact) cp.contact = {};
    if (!cp.criminalRecords) cp.criminalRecords = [];
    if ((cp.relatedPartiesInfo || []).length && cp.casePartyRole == 0) delete cp.objectKey;
    cp.relatedPartiesInfo = (cp.relatedPartiesInfo || []).map((rp) => {
      try {
        const relatedCaseParty = caseParties.find(
          r =>
            r.objectKey === rp.casePartyObjectKey ||
            (r.relatedPartiesInfo || []).some(rpi => rpi.objectKey === rp.casePartyObjectKey)
        );
        if (isProsecutorOrDefendant(relatedCaseParty)) {
          rp.relatedCasePartyRole = relatedCaseParty.casePartyRole;
        } else {
          const relatedObject = (relatedCaseParty.relatedPartiesInfo || []).find(
            rpi => rpi.objectKey === rp.casePartyObjectKey
          );
          rp.relatedCasePartyRole = relatedObject.casePartyRole;
        }
        rp.relatedPartyType = relatedCaseParty.partyType;

        const relatedOfRelatedCaseParty = caseParties.find(r =>
          (relatedCaseParty.relatedPartiesInfo || []).some(
            rpi => r.objectKey === rpi.casePartyObjectKey
          )
        );
        if (relatedOfRelatedCaseParty) {
          rp.relatedOfRelatedCasePartyObjectKey = relatedOfRelatedCaseParty.objectKey;
          rp.relatedOfRelatedCasePartyRole = relatedOfRelatedCaseParty.casePartyRole;
          rp.relatedOfRelatedPartyType = relatedOfRelatedCaseParty.partyType;
        }
      } catch {
        // eslint-disable-next-line no-console
        console.error(`There is an error related to this case party`, rp.objectKey);
      }

      return rp;
    });
    return cp;
  });

export const getBlobUrl = (base64Data, isImage) => {
  if (!base64Data) return null;
  const b64toBlob = (b64Data, contentType = '', sliceSize = 512) => {
    const byteCharacters = atob(b64Data);
    const byteArrays = [];

    for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
      const slice = byteCharacters.slice(offset, offset + sliceSize);

      const byteNumbers = new Array(slice.length);
      for (let i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i);
      }

      const byteArray = new Uint8Array(byteNumbers);
      byteArrays.push(byteArray);
    }

    const blob = new Blob(byteArrays, { type: contentType });
    return blob;
  };
  base64Data = base64Data.replace(/^data:.+;base64,/, '');
  const blob = b64toBlob(base64Data, detectMimeType(base64Data) || 'image/png');
  return URL.createObjectURL(blob);
};

export const detectMimeType = (b64) => {
  for (var s in signatures) {
    if (b64.indexOf(s) === 0) {
      return signatures[s];
    }
  }
};

const signatures = { JVBERi0: 'application/pdf' };